// 16-point compass directions, clockwise from North
export const COMPASS_DIRECTIONS = [
  'N', 'NNE', 'NE', 'ENE',
  'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW',
  'W', 'WNW', 'NW', 'NNW',
] as const;

export type CompassDirection = (typeof COMPASS_DIRECTIONS)[number];

const DIRECTION_TO_DEG: Record<string, number> = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
  E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
  W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

function windDegToCardinal(deg: number): string {
  const normalized = ((deg % 360) + 360) % 360;
  const index = Math.round(normalized / 22.5) % 16;
  return COMPASS_DIRECTIONS[index];
}

/**
 * Returns true if the current wind direction (meteorological degrees)
 * is within the list of ideal wind directions.
 */
export function isWindDirectionFavorable(windDeg: number, idealDirs: string[]): boolean {
  if (idealDirs.length === 0) return false;
  const cardinal = windDegToCardinal(windDeg);
  return idealDirs.includes(cardinal);
}

/**
 * Convert a compass direction string to meteorological degrees (0–360).
 */
export function directionToDeg(direction: string): number {
  return DIRECTION_TO_DEG[direction] ?? 0;
}

/**
 * Returns the pixel radius for a wind cone at the given zoom level.
 * Returns null when the cone should be hidden entirely (z < 14).
 */
export function getConeRadiusPx(zoom: number): number | null {
  if (zoom < 14) return null;
  if (zoom < 16) return 60;
  if (zoom < 18) return 90;
  return 120;
}

/**
 * Converts meteorological wind degrees (direction wind is FROM) to a
 * Canvas 2D unit flow vector (direction wind is going TO).
 * Canvas Y-axis is inverted relative to geographic north.
 */
export function directionVectorFromDeg(windDegFrom: number): { dx: number; dy: number } {
  const rad = ((windDegFrom + 180) % 360) * (Math.PI / 180);
  return { dx: Math.sin(rad), dy: -Math.cos(rad) };
}

/**
 * Generate a closed GeoJSON polygon ring (array of [lng, lat]) for a single
 * wind cone sector centered on the given compass direction.
 * Each sector spans ±11.25° (half of one 22.5° compass segment).
 */
export function generateWindConeRing(
  centerLng: number,
  centerLat: number,
  direction: string,
  radiusMeters: number = 80,
): [number, number][] {
  const compassCenter = DIRECTION_TO_DEG[direction] ?? 0;
  const halfWidth = 11.25;
  const steps = 10;

  const latRad = (centerLat * Math.PI) / 180;
  const latPerMeter = 1 / 111111;
  const lngPerMeter = 1 / (111111 * Math.cos(latRad));

  const ring: [number, number][] = [[centerLng, centerLat]];

  for (let i = 0; i <= steps; i++) {
    const compassDeg = compassCenter - halfWidth + (halfWidth * 2 * i) / steps;
    // Convert compass bearing to standard math angle (CCW from east axis)
    const mathRad = ((90 - compassDeg) * Math.PI) / 180;
    const dx = Math.cos(mathRad) * radiusMeters * lngPerMeter;
    const dy = Math.sin(mathRad) * radiusMeters * latPerMeter;
    ring.push([centerLng + dx, centerLat + dy]);
  }

  ring.push([centerLng, centerLat]); // close the polygon
  return ring;
}
